/**
 * The rim (doc 14): circling shoal clusters just outside the map, pulsing in
 * cold light, riled shoals red-shifted. The minimap's rim ring (doc 17 §3) is
 * the always-visible copy; these are the diegetic bodies in the water.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import { drawShoalCluster } from '../art/shoal.ts';
import type { Camera } from '../render/camera.ts';
import { TH, TW } from '../render/iso.ts';
import { OUTLINE_HOVER, type DepthPalette } from '../render/palette.ts';
import { isoDiamondRim } from '../render/primitives.ts';
import { TextureCache } from '../render/pipeline.ts';
import { PixelSurface } from '../render/surface.ts';
import type { Sim } from '../sim/sim.ts';
import type { RimShoal } from '../sim/types.ts';

export class RimView {
  private readonly views = new Map<string, Sprite>();

  private readonly sim: () => Sim;
  private readonly layer: Container;
  private readonly cache: TextureCache;
  private readonly depth: DepthPalette;
  private readonly size: number;

  constructor(sim: () => Sim, layer: Container, cache: TextureCache, depth: DepthPalette, size: number) {
    this.sim = sim;
    this.layer = layer;
    this.cache = cache;
    this.depth = depth;
    this.size = size;
  }

  orbitToWorld(orbit: number): { x: number; y: number } {
    const side = Math.floor(orbit * 4) % 4;
    const along = (orbit * 4) % 1;
    const a = along * (this.size - 1);
    const off = 2.5; // close enough that the rim is visible from home
    let ti: number;
    let tj: number;
    if (side === 0) {
      ti = a;
      tj = -off;
    } else if (side === 1) {
      ti = this.size - 1 + off;
      tj = a;
    } else if (side === 2) {
      ti = this.size - 1 - a;
      tj = this.size - 1 + off;
    } else {
      ti = -off;
      tj = this.size - 1 - a;
    }
    return { x: Math.round((ti - tj) * TW), y: Math.round((ti + tj) * TH + TH) };
  }

  private texture(shoal: RimShoal, pulse: number, selected: boolean): Texture {
    const key = `shoal:${shoal.menace}:${shoal.riled}:${pulse}:${selected}:${shoal.boss ?? ''}`;
    return this.cache.get(key, () => {
      const base = drawShoalCluster(this.depth, shoal.menace, shoal.riled, pulse);
      if (!selected && !shoal.boss) return base;
      const s = new PixelSurface(base.width + 8, base.height + 8);
      s.blit(base, 4, 4);
      if (shoal.boss) {
        // bosses carry a heavier presence at the rim: a second, wider knot
        s.px(2, s.height >> 1, this.depth.enemyGlow);
        s.px(s.width - 3, s.height >> 1, this.depth.enemyGlow);
      }
      if (selected) isoDiamondRim(s, s.width >> 1, s.height >> 1, 8, OUTLINE_HOVER, true, pulse);
      return s.resetOps();
    });
  }

  sync(selectedShoalId: string | null): void {
    const sim = this.sim();
    const pulse = (sim.state.tick >> 3) & 1;
    for (const shoal of sim.state.rim) {
      let sp = this.views.get(shoal.id);
      if (shoal.state !== 'circling') {
        if (sp) {
          sp.destroy();
          this.views.delete(shoal.id);
        }
        continue;
      }
      if (!sp) {
        sp = new Sprite();
        this.layer.addChild(sp);
        this.views.set(shoal.id, sp);
      }
      const tex = this.texture(shoal, pulse, shoal.id === selectedShoalId);
      sp.texture = tex;
      const p = this.orbitToWorld(shoal.orbit);
      sp.x = p.x - (tex.width >> 1);
      sp.y = p.y - (tex.height >> 1);
    }
  }

  pick(camera: Camera, sx: number, sy: number): RimShoal | null {
    const sim = this.sim();
    const w = camera.screenToWorld(sx, sy);
    for (const shoal of sim.state.rim) {
      if (shoal.state !== 'circling') continue;
      const p = this.orbitToWorld(shoal.orbit);
      if (Math.abs(w.x - p.x) < 18 && Math.abs(w.y - p.y) < 14) return shoal;
    }
    return null;
  }

  clear(): void {
    for (const sp of this.views.values()) sp.destroy();
    this.views.clear();
  }
}
